import {
  getPermissionNode,
  PermissionNode,
  PermissionTree,
  useAssistantPermissionsStore,
} from "app/zustand/assistantPermissionsStore";

const FULL_ACCESS_NODE: PermissionNode = {
  label: "",
  levels: ["NONE", "VIEW", "EDIT"],
  level: "EDIT",
};
const NO_ACCESS_NODE: PermissionNode = {
  label: "",
  levels: ["NONE", "VIEW", "EDIT"],
  level: "NONE",
};

export function usePermission<T extends PermissionNode | PermissionTree = PermissionNode>(
  path: string,
): T {
  const isAssistantContext = useAssistantPermissionsStore((state) => state.isAssistantContext);
  const permissions = useAssistantPermissionsStore((state) => state.permissions);

  if (!isAssistantContext) return FULL_ACCESS_NODE as unknown as T;
  return (getPermissionNode(permissions, path) ?? NO_ACCESS_NODE) as T;
}

// The entire raw tree exactly as returned by GET /assistant-access/{id}/permissions, plus the
// status of the fetch triggered from RootLayout.tsx/DashboardLeftPanel.tsx. Use this to render
// or iterate the whole catalogue (e.g. a settings screen listing every category) - for gating a
// single section, prefer usePermission(path).
//
// Usage:
//   const { data, loading, error } = useAllPermissionsData();
//   if (loading) return <Spinner />;
//   if (error) return <ErrorMessage text={error} />;
//   if (!data) return null; // not acting as an assistant
//   data.DASHBOARD.ANALYTICS.level; // "NONE" | "VIEW" | "EDIT"
//   data.RELATIONSHIPS.CONTACTS.CLIENTS.label; // "Clients"
export function useAllPermissionsData(): {
  data: PermissionTree | null;
  loading: boolean;
  error: string | null;
} {
  const data = useAssistantPermissionsStore((state) => state.permissions);
  const loading = useAssistantPermissionsStore((state) => state.isLoading);
  const error = useAssistantPermissionsStore((state) => state.error);
  return { data, loading, error };
}
